// The three documents a contractor company carries — liability, health &
// safety, pre-qualification — as 1.0's CDCompanyDetails.aspx had them, one
// current file per type. Drop a file on the row (or click Choose) to add or
// replace it; the link downloads; × removes. H&S and pre-qualification carry
// their own expiry; public liability's expiry is the company's PL Expiry field
// above (one home per fact), shown here for context only.
// Rules: docs/business-rules/companies.md.
import { useEffect, useRef, useState } from "react";
import { Alert, Button, Form, Table } from "react-bootstrap";
import { formatDate, todayInput } from "../../utils/date.js";
import {
  listCompanyFiles, uploadCompanyFile, updateCompanyFile, deleteCompanyFile, companyFileUrl,
  errorMessage, isAbortError,
} from "../../services/apiClient.js";

const TYPES = [
  { key: "public_liability", label: "Public Liability", expires: false },
  { key: "health_safety", label: "Health & Safety", expires: true },
  { key: "prequalification", label: "Pre-qualification", expires: true },
];

const kb = (n) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

function CompanyDocumentsPanel({ companyId, canWrite }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [over, setOver] = useState("");
  const pickFor = useRef(null);
  const fileInput = useRef(null);

  useEffect(() => {
    const ac = new AbortController();
    listCompanyFiles(companyId, { signal: ac.signal })
      .then((d) => { setData(d); setError(""); })
      .catch((e) => { if (!isAbortError(e)) setError(errorMessage(e, "Unable to load documents.")); });
    return () => ac.abort();
  }, [companyId]);

  async function put(docType, file) {
    if (!file || !canWrite) return;
    setBusy(docType);
    try {
      setData(await uploadCompanyFile(companyId, docType, file));
      setError("");
    } catch (e) {
      setError(errorMessage(e, "Upload failed."));
    } finally {
      setBusy("");
    }
  }
  async function setExpiry(f, value) {
    try {
      setData(await updateCompanyFile(companyId, f.id, { expires_on: value || null }));
      setError("");
    } catch (e) {
      setError(errorMessage(e, "Could not save the expiry."));
    }
  }
  async function remove(f) {
    if (!window.confirm(`Remove the ${f.label.toLowerCase()} document "${f.original_filename}"?`)) return;
    try {
      setData(await deleteCompanyFile(companyId, f.id));
      setError("");
    } catch (e) {
      setError(errorMessage(e, "Could not remove the document."));
    }
  }
  function choose(docType) {
    pickFor.current = docType;
    fileInput.current?.click();
  }
  async function onPicked(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file && pickFor.current) await put(pickFor.current, file);
  }
  const dragProps = (docType) => canWrite ? {
    onDragOver: (e) => { e.preventDefault(); if (over !== docType) setOver(docType); },
    onDragLeave: () => setOver(""),
    onDrop: (e) => { e.preventDefault(); setOver(""); put(docType, e.dataTransfer.files?.[0]); },
  } : {};

  // ISO dates compare as strings; todayInput() is the business-local today.
  const expired = (d) => !!d && d < todayInput();

  return (
    <div className="mt-4">
      <h6 className="mb-2">Documents</h6>
      {error ? <Alert variant="danger" className="py-1 small">{error}</Alert> : null}
      <input ref={fileInput} type="file" hidden onChange={onPicked} />
      <Table size="sm" className="align-middle mb-1">
        <thead>
          <tr>
            <th style={{ width: "22%" }}>Type</th>
            <th>File</th>
            <th style={{ width: "22%" }}>Expires</th>
            <th style={{ width: "10%" }} />
          </tr>
        </thead>
        <tbody>
          {TYPES.map((t) => {
            const f = data?.files.find((x) => x.doc_type === t.key) || null;
            const isOver = over === t.key;
            return (
              <tr key={t.key} {...dragProps(t.key)} className={isOver ? "table-primary" : undefined}>
                <td>{t.label}</td>
                <td>
                  {f ? (
                    <>
                      <a href={companyFileUrl(companyId, f.id)} data-hint={`${kb(f.size_bytes)}${f.imported ? " · brought across from 1.0" : ""}`}>
                        {f.original_filename}
                      </a>
                      {f.created_by_name ? <span className="text-muted small ms-2">added by {f.created_by_name} {formatDate(f.created_at)}</span> : null}
                    </>
                  ) : (
                    <span className="text-muted">{canWrite ? "Drop the document here, or " : "None"}</span>
                  )}
                  {canWrite ? (
                    <Button variant="link" size="sm" className="p-0 ms-1 align-baseline" disabled={busy === t.key} onClick={() => choose(t.key)}>
                      {busy === t.key ? "Uploading…" : f ? "Replace" : "Choose"}
                    </Button>
                  ) : null}
                </td>
                <td>
                  {!t.expires ? (
                    <span className={`small ${expired(data?.public_liability_expiry) ? "text-danger fw-bold" : "text-muted"}`}>
                      {data?.public_liability_expiry ? `PL Expiry ${formatDate(data.public_liability_expiry)}` : "PL Expiry not set"}
                    </span>
                  ) : f ? (
                    canWrite ? (
                      <Form.Control
                        type="date" size="sm" value={f.expires_on || ""}
                        className={expired(f.expires_on) ? "border-danger text-danger" : undefined}
                        onChange={(e) => setExpiry(f, e.target.value)}
                      />
                    ) : (
                      <span className={expired(f.expires_on) ? "text-danger fw-bold" : undefined}>{f.expires_on ? formatDate(f.expires_on) : "—"}</span>
                    )
                  ) : null}
                </td>
                <td className="text-end">
                  {f && canWrite ? (
                    <Button variant="link" size="sm" className="p-0 text-danger" data-hint="Remove this document" onClick={() => remove(f)}>×</Button>
                  ) : null}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
      <div className="text-muted small">
        One current document per type; a new upload replaces the old. PL expiry is set in the field above.
      </div>
    </div>
  );
}

export default CompanyDocumentsPanel;
